import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackAgentEvent } from './agentEventTracking';

const SCROLL_THRESHOLDS = [20, 50, 80];

function slugifyPart(value, fallback = 'unknown') {
  const text = (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  return text || fallback;
}

function getMarketingPageName(pathname) {
  const segments = (pathname || '').split('/').filter(Boolean);
  if (segments.length === 0) return 'landing';
  // e.g. /features/ai-news-articles -> features_ai_news_articles
  // e.g. /blog/some-slug -> blog (avoid high-cardinality slug)
  if (segments[0] === 'blog' && segments.length > 1) return 'blog_article';
  return slugifyPart(segments.join('_'), 'page');
}

/**
 * Tracks scroll depth at 20%, 50% and 80% on marketing pages.
 * Each threshold fires at most once per pathname.
 */
export default function useScrollDepthTracking() {
  const location = useLocation();
  const firedRef = useRef(new Set());

  useEffect(() => {
    firedRef.current = new Set();

    const pageName = getMarketingPageName(location.pathname);
    // Pattern: section.tab_page_modal.field_name.event_action
    // -> event_action === 'scroll'; depth percent goes in metadata.trigger_context
    const eventName = `marketing.${pageName}.page.scroll`;

    const computeDepth = () => {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
      const viewport = window.innerHeight || doc.clientHeight || 0;
      const scrollHeight = Math.max(
        doc.scrollHeight,
        body.scrollHeight,
        doc.offsetHeight,
        body.offsetHeight,
      );
      const scrollable = scrollHeight - viewport;
      if (scrollable <= 0) return 100;
      return ((scrollTop) / scrollable) * 100;
    };

    const onScroll = () => {
      const depth = computeDepth();
      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (depth >= threshold && !firedRef.current.has(threshold)) {
          firedRef.current.add(threshold);
          trackAgentEvent({
            eventName,
            metadata: {
              pathname: location.pathname,
              trigger_context: threshold,
            },
          });
        }
      });
    };

    // Check in case the page is already short enough to be fully visible.
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname]);
}
